import React, { useEffect, useLayoutEffect, useState } from "react";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import Chart from "../../../../Components/Chart/Chart";
import useChart from "../../../../Components/Chart/useChart";
import { createDateAxis, createValueAxis } from "../TrendsUtils";
import useTrend from "../useTrend";
import type { SelectedScope } from "../../../../types/scope";

type SeriesKey = "new" | "closed" | "open";

const SERIES_COLORS: Record<SeriesKey, string> = {
  new: "#607aff",
  closed: "#41425a",
  open: "#A56AF1",
};

// Single point in a chart series, derived from each trend row.
interface RiskPostureChartDatum {
  date: string;
  value: number;
  dashLength?: number;
}

// trendData state holds the three series ("new"/"closed"/"open") that feed the chart.
type RiskPostureTrendData = Record<SeriesKey, RiskPostureChartDatum[]>;

// Raw trend rows are `[date, new, closed, open]` tuples coming from parsed JSON;
// trendData from useTrend is typed `unknown`, so we narrow locally.
type TrendRow = [string, number, number, number];

export interface RiskPostureProps {
  selectedScope?: SelectedScope | null;
  filterToExecute?: string | null;
  timeScale: string;
  scopeToExecute?: string | null;
  id: string | number;
  widgetKey?: string;
  setIsReadyToExport?: ((isReady: boolean) => void) | null;
}

const RiskPosture: React.FC<RiskPostureProps> = ({
  selectedScope,
  filterToExecute,
  timeScale,
  scopeToExecute,
  id,
  widgetKey,
  setIsReadyToExport = null,
}) => {
  const {
    trendData: data,
    trendDataError,
    trendLoading,
    refetchTrend,
  } = useTrend({
    variables: {
      ...(widgetKey ? { widgetKey } : {}),
      selection: ["new", "closed_today", "open"],
      timescale: timeScale,
      filters_config: {
        filtersjson: filterToExecute,
        scopesjson: scopeToExecute,
        scopesid: selectedScope?.value?.id,
        filters_fields_to_exclude: ["actual_status"],
      },
      aggregate_functions: ["SUM", "SUM", "SUM"],
      round_functions: ["ROUND", "ROUND", "ROUND"],
    },
    skip: (!scopeToExecute && !selectedScope?.value?.id) || !filterToExecute,
  });

  const [trendData, setTrendData] = useState<RiskPostureTrendData | null>(null);
  const [max, setMax] = useState(0);
  const chartName = `RiskPostureTrend-${id}`;

  const {
    chartLoading,
    dataError,
    dataExists,
    setChartError,
    setChartDataExists,
    startLoadingChart,
    stopLoadingChart,
    createChart,
  } = useChart(setIsReadyToExport);

  const updateDashboardData = (
    data: TrendRow[],
    dataIndex: number
  ): RiskPostureChartDatum[] => {
    const dataObj: Record<string, number> = {};

    for (let i = 0; i < data.length; i += 1) {
      dataObj[data[i][0]] = data[i][dataIndex] as number;
    }

    const dataArray = Object.keys(dataObj).map((el, index) => {
      const dataVal: RiskPostureChartDatum = {
        date: el,
        value: dataObj[el],
      };

      if (data?.length >= 2 && index === data?.length - 2) {
        dataVal.dashLength = 8;
      }

      return dataVal;
    });

    return dataArray;
  };

  const clearData = () => {
    setTrendData(null);
  };

  useEffect(() => {
    if (!!trendLoading) {
      startLoadingChart();
      clearData();
      setChartError(false);
      setChartDataExists(false);
    }
  }, [trendLoading, setChartDataExists, setChartError, startLoadingChart]);

  useEffect(() => {
    if (!!trendDataError) {
      setChartError(true);
      stopLoadingChart();
    }
  }, [trendDataError, setChartError, stopLoadingChart]);

  useEffect(() => {
    if (!!data) {
      const rows = data as TrendRow[];
      const dataNewArray = updateDashboardData(rows, 1);
      const dataClosedArray = updateDashboardData(rows, 2);
      const dataOpenArray = updateDashboardData(rows, 3);

      if (
        (!dataNewArray?.length &&
          !dataClosedArray?.length &&
          !dataOpenArray?.length) ||
        ![...dataClosedArray, ...dataNewArray, ...dataOpenArray].find(
          (el) => el.value !== 0
        )
      ) {
        stopLoadingChart();
        setChartDataExists(false);
      } else if (
        dataNewArray?.length ||
        dataClosedArray?.length ||
        dataOpenArray?.length
      ) {
        setChartError(false);
        stopLoadingChart();
        setChartDataExists(true);
      }

      const dataObj: RiskPostureTrendData = {
        new: dataNewArray,
        closed: dataClosedArray,
        open: dataOpenArray,
      };

      let maxNum = 0;
      rows.forEach((item) => {
        item?.forEach((value) => {
          if ((value as number) > maxNum) {
            maxNum = value as number;
          }
        });
      });

      setMax(maxNum * 1.1);
      setTrendData(dataObj);
    }
  }, [data]);

  useLayoutEffect(() => {
    if (dataExists) {
      let [chart, legendContainer] = createChart(chartName, am4charts.XYChart);

      chart.cursor = new am4charts.XYCursor();
      chart.paddingTop = 45;
      chart.paddingRight = 40;
      chart.paddingBottom = -10;
      chart.leftAxesContainer.layout = "vertical";

      const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
      createDateAxis(dateAxis, timeScale);
      const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
      createValueAxis(valueAxis, "Findings", max);
      valueAxis.getSeriesDataItem = function (
        this: am4charts.ValueAxis,
        series: am4charts.XYSeries,
        position: number
      ) {
        const key = this.axisFieldName + this.axisLetter;
        const value = this.positionToValue(position);
        const dataItem = series.dataItems.getIndex(
          series.dataItems.findClosestIndex(
            value,
            (x) => {
              return (x as unknown as Record<string, number>)[key]
                ? (x as unknown as Record<string, number>)[key]
                : (undefined as unknown as number);
            },
            "any"
          )
        );
        return dataItem;
      } as typeof valueAxis.getSeriesDataItem;
      dateAxis.renderer.grid.template.disabled = true;

      let label = dateAxis.renderer.labels.template;
      label.wrap = true;
      label.maxWidth = 85;
      label.textAlign = "middle";

      function createSeries(s: SeriesKey, name: string) {
        let seriesColor = am4core.color(SERIES_COLORS[s]);
        let series = chart.series.push(new am4charts.LineSeries());
        series.dataFields.valueY = "value";
        series.dataFields.dateX = "date";
        series.name = name;
        series.strokeWidth = 2;
        series.tensionX = 0.8;
        series.stroke = seriesColor;
        series.propertyFields.strokeDasharray = "dashLength";

        series.tooltipText = "{name}: {value}";
        series.tooltip!.autoTextColor = false;
        series.tooltip!.label.fill = "white" as unknown as am4core.Color;
        series.fill = seriesColor;

        const bullet = series.bullets.push(new am4charts.CircleBullet());
        bullet.circle.strokeWidth = 2;
        bullet.circle.radius = 3;

        const bullethover = bullet.states.create("hover");
        bullethover.properties.scale = 1.3;

        let segment = series.segments.template;
        segment.interactionsEnabled = true;

        segment.events.on("over", function (event) {
          processOver(
            event.target.parent!.parent!
              .parent as unknown as am4charts.LineSeries
          );
        });

        segment.events.on("out", function (event) {
          processOut(
            event.target.parent!.parent!
              .parent as unknown as am4charts.LineSeries
          );
        });

        series.data = trendData?.[s] as RiskPostureChartDatum[];
        series.yAxis = valueAxis;
        return series;
      }

      createSeries("new", "New Findings");
      createSeries("closed", "Closed Findings");
      createSeries("open", "Open Findings");

      chart.legend.itemContainers.template.events.on("over", function (event) {
        processOver(
          event.target.dataItem!.dataContext as unknown as am4charts.LineSeries
        );
      });

      chart.legend.itemContainers.template.events.on("out", function (event) {
        processOut(
          event.target.dataItem!.dataContext as unknown as am4charts.LineSeries
        );
      });

      function processOver(hoveredSeries: am4charts.LineSeries) {
        hoveredSeries.toFront();

        hoveredSeries.segments.each(function (segment) {
          segment.setState("hover");
        });

        chart.series.each(function (series) {
          if (series !== hoveredSeries) {
            (series as am4charts.LineSeries).segments.each(function (segment) {
              segment.setState("dimmed");
            });
            series.bulletsContainer.setState("dimmed");
          }
        });
      }
      function processOut(hoveredSeries: am4charts.LineSeries) {
        chart.series.each(function (series) {
          (series as am4charts.LineSeries).segments.each(function (segment) {
            segment.setState("default");
          });
          series.bulletsContainer.setState("default");
        });
      }

      return () => {
        chart.dispose();
        legendContainer?.dispose();
      };
    }
  }, [dataExists, trendData, createChart]);

  return (
    <Chart
      chartLoading={chartLoading}
      chartName={chartName}
      chartError={dataError}
      chartData={dataExists}
      chartReload={refetchTrend}
    />
  );
};

export default RiskPosture;
